import { createSelectorCreator, weakMapMemoize } from "reselect";
import { shouldLogPerformance } from "utils/performance";
import type { WeakMapMemoizeOptions } from "reselect";

// Takes a function and a name, and returns a identical function that also tracks the performance of the function.
const memo =
  (matching: string) =>
  <T extends (...args: any[]) => any>(
    fun: T,
    name: string,
    options: WeakMapMemoizeOptions | undefined,
  ) => {
    return weakMapMemoize((...args: any[]) => {
      // If the name matches, we want to log the performance as the args,
      // are only used for the memoization of the result.
      if (name === matching && performance.mark) {
        const startMark = `${name}-StartMark`;
        const endMark = `${name}-EndMark`;
        const fullName = `♻️👌 Selector ${name}`;

        performance.mark(startMark);
        const result = fun(...args);
        performance.mark(endMark);
        performance.measure(fullName, startMark, endMark);
        const time = performance.getEntriesByName(fullName)[0].duration;
        if (time > 5 && shouldLogPerformance()) {
          console.log("[Profiler] ", fullName, time);
        }
        performance.clearMarks(startMark);
        performance.clearMarks(endMark);
        performance.clearMeasures(fullName);
        return result;
      }
      return fun(...args);
    }, options);
  };

export const createMarkedSelector = (
  name: string,
  options: WeakMapMemoizeOptions | undefined = undefined,
) => createSelectorCreator(memo(name), name, options);
